<template>
  <div class="metric-card">
    <div class="metric-card__icon" v-if="icon">
      <i v-if="icon.startsWith('fa-')" :class="icon"></i>
      <i v-else class="material-icons">{{ icon }}</i>
    </div>
    <div class="metric-card__body">
      <Tooltip :text="isCompact ? valueExact : ''" :active="isCompact" position="top">
        <div class="metric-card__value">{{ formattedValue }}</div>
      </Tooltip>
      <div class="metric-card__label">{{ label }}</div>
      <div class="metric-card__trend" v-if="hasPrevious">
        <span class="trend-badge" :class="trendClass">
          <i class="material-icons trend-icon">{{ trendIcon }}</i>
          {{ trendText }}
        </span>
        <Tooltip :text="isPrevCompact ? prevExact : ''" :active="isPrevCompact" position="top">
          <span class="metric-card__prev">vs {{ formattedPrev }}</span>
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'
export default {
  name: 'MetricCard',
  components: { Tooltip },
  props: {
    value: { type: Number, default: 0 },
    previousValue: { type: Number, default: null },
    label: { type: String, required: true },
    icon: { type: String, default: null },
    format: { type: String, default: 'number' }, // 'number' | 'percent'
  },
  computed: {
    hasPrevious() {
      return this.previousValue !== null && this.previousValue !== undefined
    },
    formattedValue() {
      if (this.format === 'percent') return `${(this.value || 0).toFixed(1)}%`
      return this._fmtCompact(this.value || 0)
    },
    formattedPrev() {
      if (this.format === 'percent') return `${(this.previousValue || 0).toFixed(1)}%`
      return this._fmtCompact(this.previousValue || 0)
    },
    diff() {
      if (!this.hasPrevious || !this.previousValue) return null
      return ((this.value - this.previousValue) / this.previousValue) * 100
    },
    trendClass() {
      if (this.diff === null) return 'flat'
      if (this.diff > 0) return 'up'
      if (this.diff < 0) return 'down'
      return 'flat'
    },
    trendIcon() {
      if (this.diff > 0) return 'trending_up'
      if (this.diff < 0) return 'trending_down'
      return 'trending_flat'
    },
    trendText() {
      if (this.diff === null) return '—'
      const abs = Math.abs(this.diff)
      const formatted = abs > 0 && abs < 1 ? this.diff.toFixed(1) : Math.round(this.diff).toString()
      return `${this.diff > 0 ? '+' : ''}${formatted}%`
    },
    isCompact() {
      return this.format !== 'percent' && (this.value || 0) >= 1000
    },
    isPrevCompact() {
      return this.format !== 'percent' && (this.previousValue || 0) >= 1000
    },
    valueExact() {
      return (this.value || 0).toLocaleString()
    },
    prevExact() {
      return (this.previousValue || 0).toLocaleString()
    },
  },
  methods: {
    _fmtCompact(v) {
      if (v >= 1000000) return (v / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (v >= 1000)    return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return v.toLocaleString()
    },
  },
}
</script>

<style scoped>
.metric-card {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  box-shadow: 0 2px 5.6px rgba(107, 31, 77, 0.08);
  border: 1px solid #f1edf5;
  min-width: 160px;
  flex: 1;
}
.metric-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 5.6px;
  background: #f1edf5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B1F4D;
  font-size: 18px;
  flex-shrink: 0;
}
.metric-card__body { flex: 1; min-width: 0; }
.metric-card__value {
  font-size: 26px;
  font-weight: 700;
  color: #121826;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.metric-card__label {
  font-size: 12px;
  color: #555E75;
  margin-top: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.metric-card__trend {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}
.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 20px;
}
.trend-icon { font-size: 14px; }
.trend-badge.up   { background: #e6f9f0; color: #1a9e5e; }
.trend-badge.down { background: #fdf0f0; color: #d93025; }
.trend-badge.flat { background: #f1edf5; color: #6B1F4D; }
.metric-card__prev {
  font-size: 11px;
  color: #9ba3b7;
}
</style>
